import { ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';
import type { UiSize, UiTone } from '../contracts';

const badgeVariants = cva('inline-flex items-center rounded-full font-medium', {
  variants: {
    variant: {
      primary: 'bg-primary-100 text-primary-600',
      success: 'bg-success-100 text-success-600',
      warning: 'bg-warning-100 text-warning-600',
      error: 'bg-error-100 text-error-600',
      gray: 'bg-gray-100 text-gray-600',
    },
    size: {
      sm: 'px-2 py-0.5 text-[11px]',
      md: 'px-2.5 py-0.5 text-xs',
      lg: 'px-3 py-1 text-sm',
    },
  },
  defaultVariants: {
    variant: 'gray',
    size: 'md',
  },
});

const toneToVariant: Record<UiTone, NonNullable<BadgeProps['variant']>> = {
  neutral: 'gray',
  current: 'primary',
  success: 'success',
  warning: 'warning',
  danger: 'error',
  locked: 'gray',
};

type BadgeVariantProps = Omit<VariantProps<typeof badgeVariants>, 'size'>;

export interface BadgeProps extends BadgeVariantProps {
  children: ReactNode;
  className?: string;
  tone?: UiTone;
  size?: UiSize;
}

export function Badge({ variant, tone, size, children, className }: BadgeProps) {
  const resolvedVariant = variant ?? (tone ? toneToVariant[tone] : undefined);
  return (
    <span className={cn(badgeVariants({ variant: resolvedVariant, size }), className)}>
      {children}
    </span>
  );
}
